import React, { useMemo, useState } from 'react';
import { sampleRoaches } from '../../data/sampleRoachData';
import AILightComponent from "../../../../components/AILightComponent";

const PulseBar = ({ value, color = "bg-cyan-400" }) => (
  <div className="w-full bg-gray-800 rounded-full h-2">
    <div
      className={`${color} h-2 rounded-full transition-all duration-500`}
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

const getEvolutionStage = (avgScore, streak, totalDays) => {
  const scoreWeight       = avgScore * 4;
  const streakWeight      = Math.min(streak, 15) * 2;
  const consistencyWeight = Math.min(totalDays, 30) * 1;
  return Math.min(10, Math.floor((scoreWeight + streakWeight + consistencyWeight) / 10));
};

const getStreak = (dates) => {
  if (!dates.length) return 0;
  let streak = 1;
  for (let i = 0; i < dates.length - 1; i++) {
    const diff = Math.round((new Date(dates[i]) - new Date(dates[i + 1])) / (1000 * 3600 * 24));
    if (diff === 1) streak++;
    else break;
  }
  return streak;
};

const getTrend = (roaches) => {
  if (roaches.length < 4) return null;
  const half      = Math.ceil(roaches.length / 2);
  const recentAvg = roaches.slice(0, half).reduce((s, r) => s + (r.avgScore || 0), 0) / half;
  const olderAvg  = roaches.slice(half).reduce((s, r) => s + (r.avgScore || 0), 0) / (roaches.length - half);
  const delta     = recentAvg - olderAvg;
  if (delta >  0.3) return { label: "Improving ↗", color: "text-green-400" };
  if (delta < -0.3) return { label: "Declining ↘",  color: "text-red-400" };
  return               { label: "Steady →",    color: "text-yellow-400" };
};

const getMilestones = (roaches) => {
  const sorted     = [...roaches].sort((a, b) => new Date(a.roachDate) - new Date(b.roachDate));
  const milestones = [];
  const firstHigh  = sorted.find(r => (r.avgScore || 0) >= 8);
  if (firstHigh) milestones.push({ label: "First 8+ score", date: firstHigh.roachDate, icon: "⭐" });
  const best = sorted.reduce((a, b) => (a.avgScore || 0) > (b.avgScore || 0) ? a : b, sorted[0]);
  if (best) milestones.push({ label: `Personal best ${best.avgScore?.toFixed(2)}`, date: best.roachDate, icon: "🏆" });
  if (sorted.length >= 7)  milestones.push({ label: "7 days in",  date: sorted[6].roachDate,  icon: "🔥" });
  if (sorted.length >= 30) milestones.push({ label: "30 days in", date: sorted[29].roachDate, icon: "💎" });
  return milestones;
};

const getEvolutionMessage = (stage) => {
  const messages = [
    "Just getting started. Every roach begins here.",
    "Stirring. The first habits are forming.",
    "Awakening. Patterns are emerging.",
    "Adapting. You're finding your rhythm.",
    "Surviving. Consistency is building.",
    "Thriving. The compound effect kicks in.",
    "Evolving. Hard days no longer stop you.",
    "Dominating. Your baseline has shifted.",
    "Unstoppable. Most quit long before this.",
    "Legendary. You are the 1%.",
    "APEX ROACH. Fully evolved.",
  ];
  return messages[Math.min(stage, 10)];
};

// ── Main Component ────────────────────────────────────────────────────────────

const PulseEvolutionScreen = ({ pulseContext = "Matrix" }) => {
  const history = sampleRoaches;

  const [enableAIInsight, setEnableAIInsight] = useState(false);
  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleAIInsight = () => setEnableAIInsight((p) => !p);
  const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

  const stats = useMemo(() => {
    if (!history?.length) return null;

    // Filter by pulseContext just like PulseStreakCalendar
    const filtered = pulseContext === "Matrix"
      ? history
      : history.filter(r => String(r.pulseContext).trim() === pulseContext);

    if (!filtered.length) return null;

    const sorted = [...filtered].sort((a, b) => new Date(b.roachDate) - new Date(a.roachDate));

    // Unique active dates for this context
    const uniqueDates = [...new Set(
      sorted.map(r => String(r.roachDate).split('T')[0].split(' ')[0].trim())
    )].sort((a, b) => new Date(b) - new Date(a));

    const totalDays = uniqueDates.length;
    const avgScore  = sorted.reduce((s, r) => s + (r.avgScore || 0), 0) / sorted.length;
    const bestDay   = sorted.reduce((a, b) => (a.avgScore || 0) > (b.avgScore || 0) ? a : b);
    const worstDay  = sorted.reduce((a, b) => (a.avgScore || 0) < (b.avgScore || 0) ? a : b);
    const streak    = getStreak(uniqueDates);
    const stage     = getEvolutionStage(avgScore, streak, totalDays);
    const trend     = getTrend(sorted);
    const milestones = getMilestones(sorted);

    // Context breakdown — only meaningful in Matrix mode
    const contextBreakdown = pulseContext === "Matrix"
      ? ["Overall", "Home", "Work", "Campus", "Learning", "Social", "Environment"].map(ctx => {
          const entries = history.filter(r => String(r.pulseContext).trim() === ctx);
          if (!entries.length) return null;
          const avg = entries.reduce((s, r) => s + (r.avgScore || 0), 0) / entries.length;
          return { name: ctx, avg, count: entries.length };
        }).filter(Boolean).sort((a, b) => b.avg - a.avg)
      : [];

    return { sorted, totalDays, avgScore, bestDay, worstDay, streak, stage, trend, milestones, contextBreakdown };
  }, [history, pulseContext]);

  if (!stats) return (
    <div className="text-gray-500 text-center p-8">
      No data for {pulseContext === "Matrix" ? "any context" : pulseContext} yet.
    </div>
  );

  const { sorted, totalDays, avgScore, bestDay, worstDay, streak, stage, trend, milestones, contextBreakdown } = stats;
  const isMatrix = pulseContext === "Matrix";

  return (
    <div className="space-y-4">

      {/* ── Stage Card ── */}
      <div className="bg-[#0f172a] p-5 rounded-xl border border-gray-800">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-widest">
              {isMatrix ? "Matrix Evolution" : `${pulseContext} Evolution`}
            </div>
            <div className="text-5xl font-black text-green-400 mt-1">{stage}</div>
            <div className="text-xs text-gray-400 mt-1">{getEvolutionMessage(stage)}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-cyan-400">{totalDays}</div>
            <div className="text-xs text-gray-500">active days</div>
            {trend && (
              <div className={`text-xs font-bold mt-1 ${trend.color}`}>{trend.label}</div>
            )}
          </div>
        </div>
        <div className="mt-4">
          <PulseBar value={(stage / 10) * 100} color="bg-green-400" />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>Stage 0</span><span>Stage 10</span>
          </div>
        </div>
      </div>

      {/* ── Score Summary ── */}
      <div className="bg-[#0f172a] p-5 rounded-xl border border-gray-800">
        <div className="text-sm font-bold mb-4">
          📊 {isMatrix ? "Matrix" : pulseContext} Score Summary
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-900 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400">Average</div>
            <div className="text-2xl font-black text-cyan-400">{avgScore.toFixed(1)}</div>
          </div>
          <div className="bg-gray-900 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400">Best</div>
            <div className="text-2xl font-black text-green-400">{bestDay.avgScore?.toFixed(2)}</div>
          </div>
          <div className="bg-gray-900 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400">Worst</div>
            <div className="text-2xl font-black text-red-400">{worstDay.avgScore?.toFixed(2)}</div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="bg-gray-900 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400">Current Streak</div>
            <div className="text-2xl font-black text-yellow-400">🔥 {streak}</div>
          </div>
          <div className="bg-gray-900 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400">Total Entries</div>
            <div className="text-2xl font-black text-purple-400">{sorted.length}</div>
          </div>
        </div>
      </div>

      {/* ── Context Breakdown — Matrix only ── */}
      {isMatrix && contextBreakdown.length > 0 && (
        <div className="bg-[#111827] p-5 rounded-xl border border-gray-800">
          <div className="text-sm font-bold mb-3">🏷️ Context Breakdown</div>
          <div className="space-y-2">
            {contextBreakdown.map((ctx, i) => (
              <div key={i}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-300">{ctx.name}</span>
                  <span className="text-cyan-400 font-bold">
                    {ctx.avg.toFixed(1)} avg · {ctx.count} entries
                  </span>
                </div>
                <PulseBar value={(ctx.avg / 10) * 100} color={i === 0 ? "bg-green-400" : "bg-cyan-600"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Milestones ── */}
      {milestones.length > 0 && (
        <div className="bg-[#111827] p-5 rounded-xl border border-gray-800">
          <div className="text-sm font-bold mb-3">🏅 Milestones</div>
          <div className="space-y-2">
            {milestones.map((m, i) => (
              <div key={i} className="flex justify-between items-center py-1 border-b border-gray-800 last:border-0">
                <span className="text-sm">{m.icon} {m.label}</span>
                <span className="text-xs text-gray-500">
                  {new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent Journey ── */}
      <div className="bg-[#111827] p-5 rounded-xl border border-gray-800">
        <div className="text-sm font-bold mb-3">
          🗓️ Recent {isMatrix ? "Matrix" : pulseContext} Journey
        </div>
        {sorted.slice(0, 5).map((h, i) => (
          <div key={i} className="flex justify-between items-center py-2 border-b border-gray-800 last:border-0">
            <div>
              <div className="text-sm text-gray-200">
                {new Date(h.roachDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
              {isMatrix && <div className="text-xs text-gray-500">{h.pulseContext}</div>}
            </div>
            <div className="text-right">
              <span className="font-bold text-cyan-400">{h.avgScore?.toFixed(2)}</span>
              <span className="text-xs text-gray-500 ml-1">→ {h.type}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── AI Coach ── */}
      <div className="flex flex-col w-full gap-2">
        <button
          onClick={toggleGenAIHelp}
          className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
            enableGenAIHelp
              ? "bg-purple-500 text-white"
              : "bg-[#111827] text-purple-300 border border-purple-500/30"
          }`}
        >
          {enableGenAIHelp ? "🤖 AI Coach Active" : "🚀 Ask GenAI Coach"}
        </button>

        {enableGenAIHelp && (
          <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
            <AILightComponent
              mode="daily_life_mood_dashboard"
              param1={`${isMatrix ? "Matrix" : pulseContext} Evolution Analysis`}
              param2={`
              Context: ${isMatrix ? "Full Matrix (all contexts)" : pulseContext}
              Evolution Stage: ${stage}/10
              Stage Message: ${getEvolutionMessage(stage)}
              Trend: ${trend?.label || "Not enough data"}

              Score Summary:
              - Average Score: ${avgScore.toFixed(2)}
              - Best Day Score: ${bestDay.avgScore?.toFixed(2)}
              - Worst Day Score: ${worstDay.avgScore?.toFixed(2)}
              - Current Streak: ${streak} days
              - Total Active Days: ${totalDays}
              - Total Entries: ${sorted.length}

              ${isMatrix && contextBreakdown.length > 0 ? `Context Breakdown:
              ${contextBreakdown.map(c => `- ${c.name}: ${c.avg.toFixed(1)} avg (${c.count} entries)`).join('\n')}` : ''}

              ${milestones.length > 0 ? `Milestones Achieved:
              ${milestones.map(m => `- ${m.icon} ${m.label}`).join('\n')}` : ''}

              Recent 5 Entries:
              ${sorted.slice(0, 5).map(h => 
                `- ${new Date(h.roachDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} | ${h.pulseContext} | Score: ${h.avgScore?.toFixed(2)} | ${h.type}`
              ).join('\n')}

              Please analyze:
              - Behavioral growth pattern
              - Consistency and streak quality
              - Strongest and weakest contexts
              - Burnout or plateau risk
              - Identity evolution stage
              - Suggested next 3 actions
             `}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PulseEvolutionScreen;